import { Box } from "@mui/system";
import { Alert, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import GroupsIcon from "@mui/icons-material/Groups";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useEffect, useState } from "react";
import Loading from "components/Loading";
import axios from "axios";
import { IsUnauthorizedError, useLogin } from "providers/Login";
import { useLocation, useNavigate } from "react-router";
import { useNotification } from "providers/Notification";
import { FormDrawer } from "components/FormDrawer";
import { ConfirmDialog } from "components/ConfirmDialog";
import DataTable, { useTableColumnState, SaveColumnsState } from "components/Table";
import StatusLabel, { StatusColor } from "components/StatusLabel";
import { DirectoryWizard } from "./DirectoryWizard";
import {
    Directory,
    DirectoryType,
    DirectoryStatus,
    directoryDescriptions,
    directoryDisplayNames,
    canBeEnabled,
    canManageGroups,
    enabledLabel,
    hasChildren,
    ownEnabled,
} from "./_components/Types";
import { directoryIcons } from "./_components/BrandIcons";
import * as directoriesApi from "./_components/directoriesApi";
import { formatDate } from "helpers/dateFormat";

type columnsType = "name" | "type" | "status" | "description" | "enabled" | "createdat" | "updatedat";

const ALL_TYPES: DirectoryType[] = ["google", "azure", "okta", "ldap"];

const statusDisplayNames: { [key in DirectoryStatus]: string } = {
    "pending": "Pending",
    "healthy": "Healthy",
    "degraded": "Degraded",
    "auth_failed": "Auth Failed",
    "sync_failed": "Sync Failed",
};

const statusColors: { [key in DirectoryStatus]: string } = {
    "pending": "text.secondary",
    "healthy": "success.main",
    "degraded": "warning.main",
    "auth_failed": "error.main",
    "sync_failed": "error.main",
};

function awaitingConsent(directory: Directory): boolean {
    return directory.type === "google" && directory.config.pendingCredentialsName !== undefined;
}

function statusText(directory: Directory): string {
    if (directory.enabled) {
        return statusDisplayNames[directory.status];
    }
    if (awaitingConsent(directory)) {
        return "Waiting for consent";
    }
    if (directory.status === "pending") {
        return statusDisplayNames.pending;
    }
    return "Idle";
}

function statusColor(directory: Directory): StatusColor {
    if (directory.enabled) {
        return statusColors[directory.status];
    }
    if (awaitingConsent(directory)) {
        return "warning.main";
    }
    if (directory.status === "pending") {
        return statusColors.pending;
    }
    return "text.disabled";
}

function flattenDirectories(directories: Directory[], expandedIds: string[]): Directory[] {
    const present = new Set(directories.map((d) => d.id));
    return directories
        .filter((d) => d.parentId === undefined || !present.has(d.parentId))
        .flatMap((parent) => {
            const children = directories.filter((d) => d.parentId === parent.id);
            if (children.length === 0 || !expandedIds.includes(parent.id)) {
                return [parent];
            }
            return [parent, ...children];
        });
}

function Integrations() {
    const notification = useNotification();
    const login = useLogin();
    const navigate = useNavigate();
    const location = useLocation();
    const consentParams = new URLSearchParams(location.search);
    const consentResult = consentParams.get("oauth_success");
    const consentDirectoryId = consentParams.get("directoryId");
    const consentConnectionError = consentParams.get("connectionError");

    const [finishedLoading, setFinishedLoading] = useState(false);
    const [directories, setDirectories] = useState<Directory[]>([]);
    const [currentAuthenticationMode, setCurrentAuthenticationMode] = useState<"saml" | "proxy" | "none">("none");
    const [expandedIds, setExpandedIds] = useState<string[]>([]);
    const [googleSetup, setGoogleSetup] = useState<directoriesApi.GoogleDirectorySetup | undefined>();

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editingDirectory, setEditingDirectory] = useState<Directory | undefined>();
    const [wizardStep, setWizardStep] = useState<"setup" | "consent" | "domains" | undefined>();
    const [creatingType, setCreatingType] = useState<DirectoryType | undefined>();
    const [directoryBeingDeleted, setDirectoryBeingDeleted] = useState<Directory | undefined>();
    const [isDeleting, setIsDeleting] = useState(false);

    const [columnsState, setColumnsState] = useTableColumnState<columnsType>([
        { columnId: "name", width: "18%" },
        { columnId: "type", width: "14%" },
        { columnId: "status", width: "12%" },
        { columnId: "description", width: "30%" },
        { columnId: "enabled", width: "10%" },
        { columnId: "createdat", width: "8%" },
        { columnId: "updatedat", width: "8%" },
    ]);

    const req = login.GetAxios();

    const loadData = async (withAuthenticationMode = false, reopenDirectoryId?: string) => {
        setFinishedLoading(false);
        try {
            if (!req) {
                return;
            }

            const [list, mode, google] = await Promise.all([
                directoriesApi.listDirectories(req),
                withAuthenticationMode ? req.get("/api/config") : Promise.resolve(undefined),
                directoriesApi.getGoogleSetup(req).catch(() => undefined),
            ]);

            setDirectories(list);
            setExpandedIds(list.filter((d) => d.parentId === undefined).map((d) => d.id));
            setGoogleSetup(google);

            const consented = reopenDirectoryId
                ? list.find((d) => d.id === reopenDirectoryId)
                : undefined;
            if (consented) {
                setEditingDirectory(consented);
                setWizardStep("consent");
            }

            if (mode) {
                setCurrentAuthenticationMode(mode.data.authenticationMode || "none");
            }
        } catch (err) {
            if (IsUnauthorizedError(err)) {
                return;
            }
            notification.Display({
                title: "Error",
                message: "Error fetching configuration data",
                type: "error",
            });
        } finally {
            setFinishedLoading(true);
        }
    };

    useEffect(() => {
        loadData(true, consentDirectoryId || undefined);
        reportConsentResult();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        SaveColumnsState("directories", columnsState);
    }, [columnsState]);

    const isParent = (directory: Directory) => hasChildren(directory, directories);

    const toggleExpanded = (id: string) => {
        setExpandedIds((current) => current.includes(id)
            ? current.filter((x) => x !== id)
            : current.concat(id));
    };

    const setEnabled = async (directory: Directory, enabled: boolean) => {
        if (!req) {
            return;
        }
        try {
            await directoriesApi.setDirectoryEnabled(req, directory.id, enabled);
            await loadData();
        } catch (err) {
            const reason = axios.isAxiosError(err)
                ? String((err.response?.data as { message?: string } | undefined)?.message || "")
                : "";
            notification.Display({
                title: "Error",
                message: reason || "Error changing directory status",
                type: "error",
            });
            console.error(err);
        }
    };

    const reportConsentResult = () => {
        if (consentResult === null) {
            return;
        }
        const params = new URLSearchParams(location.search);
        params.delete("oauth_success");
        params.delete("directoryId");
        params.delete("connectionError");
        const query = params.toString();
        navigate(`${location.pathname}${query ? `?${query}` : ""}`, { replace: true });
        notification.Display(consentResult === "true"
            ? {
                title: "Success",
                message: "Google consent granted and the connection test succeeded.",
                type: "success",
            }
            : {
                title: consentConnectionError ? "Saved disabled" : "Error",
                message: consentConnectionError
                    ? `Google consent was saved, but the connection test failed and the integration was saved disabled: ${consentConnectionError}`
                    : "Google did not grant consent.",
                type: "error",
            });
    };

    const confirmDelete = async () => {
        if (!directoryBeingDeleted || !req) {
            return;
        }
        setIsDeleting(true);
        try {
            await directoriesApi.deleteDirectory(req, directoryBeingDeleted.id);
            notification.Display({
                title: "Success",
                message: `${directoryBeingDeleted.name} removed.`,
                type: "success",
            });
            setDirectoryBeingDeleted(undefined);
            await loadData();
        } catch (err) {
            notification.Display({
                title: "Error",
                message: "Error removing directory",
                type: "error",
            });
            console.error(err);
        }
        setIsDeleting(false);
    };

    const closeWizard = () => {
        setEditingDirectory(undefined);
        setCreatingType(undefined);
        setWizardStep(undefined);
        loadData();
    };

    const deletedChildCount = directoryBeingDeleted
        ? directories.filter((d) => d.parentId === directoryBeingDeleted.id).length
        : 0;

    const tableData = flattenDirectories(directories, expandedIds);

    return <>
        <Loading finished={finishedLoading}>
            <Box display="flex">
                <Box>
                    <Typography variant="h1">Directory Integrations</Typography>
                </Box>
                <Button
                    id="add-directory-integration-button"
                    sx={{ marginLeft: "auto" }}
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setAddDialogOpen(true)}
                >
                    Add Directory Integration
                </Button>
            </Box>

            {currentAuthenticationMode === "none" ? <Alert
                sx={{ marginTop: "10px" }}
                severity="warning"
            >
                To use directory integrations an authentication method must be enabled.
            </Alert> : ""}

            <DataTable
                data={tableData}
                finishedLoading={finishedLoading}
                columnsState={columnsState}
                onSortChanged={setColumnsState}
                columnsDataId="datagrid_directoriestable_columns"
                noDataText="No directory integrations configured yet."
                columns={{
                    name: {
                        displayName: "Name",
                        render: (x) => <Box sx={{ display: "flex", alignItems: "center" }}>
                            {isParent(x) && <IconButton
                                id={`expand-directory-${x.id}-button`}
                                size="small"
                                sx={{ marginRight: "2px" }}
                                onClick={() => toggleExpanded(x.id)}
                            >
                                {expandedIds.includes(x.id)
                                    ? <KeyboardArrowDownIcon fontSize="small" />
                                    : <KeyboardArrowRightIcon fontSize="small" />}
                            </IconButton>}
                            <span style={{ paddingLeft: x.parentId !== undefined ? "28px" : undefined }}>
                                {x.name}
                            </span>
                        </Box>,
                    },
                    type: {
                        displayName: "Type",
                        render: (x) => {
                            const BrandIcon = directoryIcons[x.type];
                            return <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <BrandIcon sx={{ fontSize: "18px", flexShrink: 0 }} />
                                <span>{directoryDisplayNames[x.type]}</span>
                            </Box>;
                        },
                    },
                    status: {
                        displayName: "Status",
                        render: (x) => isParent(x) ? "—" : <StatusLabel label={statusText(x)} color={statusColor(x)} />,
                    },
                    description: {
                        displayName: "Description",
                        render: (x) => x.description,
                    },
                    enabled: {
                        displayName: "Enabled",
                        render: (x) => {
                            const { label, running } = enabledLabel(x);
                            return <StatusLabel label={label} color={running ? "success.main" : label === "Disabled" ? "error.main" : "warning.main"} />;
                        },
                    },
                    createdat: {
                        displayName: "Created",
                        render: (x) => formatDate(new Date(x.createdAt)),
                    },
                    updatedat: {
                        displayName: "Updated",
                        render: (x) => formatDate(new Date(x.updatedAt)),
                    },
                }}
                rowOptionsMenu={[
                    {
                        text: "Manage groups",
                        icon: <GroupsIcon fontSize="small" />,
                        isVisible: (x) => canManageGroups(x),
                        onClick: (x) => navigate(`/directory/${x.id}/groups`),
                    },
                    {
                        // A forest domain toggles its own switch; the forest's stays the master switch.
                        text: (x) => ownEnabled(x) ? "Disable" : "Enable",
                        icon: <PowerSettingsNewIcon fontSize="small" />,
                        isVisible: (x) => x.parentId !== undefined || x.enabled || canBeEnabled(x),
                        onClick: (x) => setEnabled(x, !ownEnabled(x)),
                    },
                    {
                        text: "Edit",
                        icon: <EditIcon fontSize="small" />,
                        // Read-only, built-in fixture - not editable through the wizard.
                        isVisible: (x) => x.type !== "proxy",
                        onClick: (x) => setEditingDirectory(x),
                    },
                    {
                        text: "Delete",
                        icon: <DeleteIcon fontSize="small" />,
                        // Permanent fixture - never deletable, not even by an admin.
                        isVisible: (x) => x.type !== "proxy",
                        onClick: (x) => setDirectoryBeingDeleted(x),
                    },
                ]}
            />
        </Loading>

        <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Add Directory Integration</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "5px" }}>
                    {ALL_TYPES.map(type => {
                        const BrandIcon = directoryIcons[type];
                        return <Button
                            key={type}
                            id={`add-directory-${type}-button`}
                            variant="outlined"
                            color="inherit"
                            onClick={() => {
                                setAddDialogOpen(false);
                                setCreatingType(type);
                            }}
                            sx={{
                                justifyContent: "flex-start",
                                textAlign: "left",
                                textTransform: "none",
                                padding: "12px 14px",
                                borderColor: "divider",
                                "&:hover": { borderColor: "primary.main", backgroundColor: "action.hover" },
                            }}
                        >
                            <BrandIcon sx={{ fontSize: "28px", marginRight: "14px", flexShrink: 0 }} />
                            <Box>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {directoryDisplayNames[type]}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {directoryDescriptions[type]}
                                </Typography>
                            </Box>
                            <KeyboardArrowRightIcon sx={{ marginLeft: "auto", color: "text.secondary", flexShrink: 0 }} />
                        </Button>;
                    })}
                </Box>
            </DialogContent>
        </Dialog>

        <ConfirmDialog
            open={directoryBeingDeleted !== undefined}
            title="Delete Directory Integration"
            message={deletedChildCount > 0
                ? `Are you sure you want to delete ${directoryBeingDeleted?.name}? Its ${deletedChildCount} domain ${deletedChildCount === 1 ? "row" : "rows"} will be removed too.`
                : `Are you sure you want to delete ${directoryBeingDeleted?.name}?`}
            confirmLabel="Delete"
            loading={isDeleting}
            onConfirm={confirmDelete}
            onCancel={() => setDirectoryBeingDeleted(undefined)}
        />

        <FormDrawer open={editingDirectory !== undefined || creatingType !== undefined} onClose={closeWizard}>
            {editingDirectory && <DirectoryWizard
                key={`${editingDirectory.id}:${wizardStep || ""}`}
                type={editingDirectory.type}
                directory={editingDirectory}
                childDirectories={directories.filter((d) => d.parentId === editingDirectory.id)}
                googleSetup={googleSetup}
                initialStep={wizardStep}
                onExit={closeWizard}
                onSaved={closeWizard}
                onManageGroups={(id) => navigate(`/directory/${id}/groups`)}
            />}
            {creatingType && <DirectoryWizard
                type={creatingType}
                googleSetup={googleSetup}
                onExit={closeWizard}
                onSaved={closeWizard}
                onManageGroups={(id) => navigate(`/directory/${id}/groups`)}
            />}
        </FormDrawer>
    </>;
}

export default Integrations;
